import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowRight, CheckCircle, Building2, Briefcase, FileText, Globe, Users, TrendingUp, ShieldCheck, Award } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { AnimatedCounter } from '../components/ui/AnimatedCounter';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function Home() {
  const [activeFaq, setActiveFaq] = useState<number | null>(0);
  return <div className="bg-navy-900" data-id="element-267">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-id="element-268">
        <div className="absolute inset-0 z-0" data-id="element-269">
          <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=2075&q=80" alt="Luxury Bali Villa" className="w-full h-full object-cover opacity-40" data-id="element-270" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/80 to-transparent" data-id="element-271"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-transparent to-transparent" data-id="element-272"></div>
        </div>

        <div className="max-w-content mx-auto px-6 md:px-12 relative z-10 w-full" data-id="element-273">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl" data-id="element-274">
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-6" data-id="element-275">
              <div className="h-px w-12 bg-gold" data-id="element-276"></div>
              <span className="font-sub text-gold uppercase tracking-widest text-sm font-semibold" data-id="element-277">
                Premium Corporate Services
              </span>
            </motion.div>

            <motion.h1 variants={fadeUp} className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6" data-id="element-278">
              Your Trusted Partner for{' '}
              <span className="text-gold" data-id="element-279">Property & Business</span> Success
            </motion.h1>

            <motion.p variants={fadeUp} className="font-body text-lg md:text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl" data-id="element-280">
              Marvesta Corp Bali provides integrated Property Management,
              Accounting, Tax, Legal, Licensing, Visa, and Business Support
              services throughout Indonesia.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4" data-id="element-281">
              <Button to="/contact" data-id="element-282">Book Consultation</Button>
              <Button href="https://wa.me/6282326357189" variant="outline" icon={<ArrowRight size={18} data-id="element-284" />} data-id="element-283">
                WhatsApp Us
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trusted Clients Section */}
      <section className="py-16 bg-navy-800 border-y border-navy-700" data-id="element-285">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-286">
          <p className="text-center font-sub text-gray-400 uppercase tracking-widest text-sm mb-10" data-id="element-287">
            Trusted by industry leaders across
          </p>
          <div className="flex flex-wrap justify-center gap-x-12 gap-y-6 opacity-60" data-id="element-288">
            {['Villa Owners', 'Property Investors', 'Hospitality', 'Hotels & Resorts', 'PT PMA', 'Foreign Investors', 'SMEs'].map((client, i) => <span key={i} className="font-heading text-xl md:text-2xl text-white font-semibold" data-id="element-289">
                {client}
              </span>)}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-navy-900" data-id="element-290">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-291">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-center max-w-3xl mx-auto mb-16" data-id="element-292">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-293">
              Excellence in Every Detail
            </h2>
            <p className="font-body text-gray-400 text-lg" data-id="element-294">
              We combine international corporate standards with deep local
              expertise to deliver unparalleled business solutions in Bali.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-295">
            {[{
            icon: <Building2 size={32} data-id="element-296" />,
            title: 'Integrated Solutions',
            desc: 'End-to-end management from property operations to corporate tax.'
          }, {
            icon: <ShieldCheck size={32} data-id="element-297" />,
            title: 'Regulatory Compliance',
            desc: 'Strict adherence to Indonesian laws, ensuring your business is protected.'
          }, {
            icon: <TrendingUp size={32} data-id="element-298" />,
            title: 'Financial Transparency',
            desc: 'Clear, accurate, and timely reporting for informed decision-making.'
          }, {
            icon: <Award size={32} data-id="element-299" />,
            title: 'Professional Team',
            desc: 'Experienced consultants, accountants, and legal experts at your service.'
          }].map((feature, i) => <motion.div key={i} variants={fadeUp} className="bg-navy-800 p-8 rounded-card border border-navy-700 hover:border-gold/30 transition-colors group" data-id="element-300">
                <div className="w-16 h-16 bg-navy-700 rounded-2xl flex items-center justify-center text-gold mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-301">
                  {feature.icon}
                </div>
                <h3 className="font-heading text-2xl font-semibold text-white mb-4" data-id="element-302">
                  {feature.title}
                </h3>
                <p className="font-body text-gray-400 leading-relaxed" data-id="element-303">
                  {feature.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-navy-800 relative" data-id="element-304">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-305">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6" data-id="element-306">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="max-w-2xl" data-id="element-307">
              <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-308">
                Our Premium Services
              </h2>
              <p className="font-body text-gray-400 text-lg" data-id="element-309">
                Comprehensive corporate solutions designed for investors,
                property owners, and businesses establishing their presence in
                Indonesia.
              </p>
            </motion.div>
            <Button to="/services" variant="outline" data-id="element-310">
              View All Services
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-311">
            {[{
            title: 'Property Management',
            desc: 'Maximizing asset value through professional operations, revenue optimization, and exceptional guest experiences.',
            img: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
            link: '/property-management'
          }, {
            title: 'Accounting, Tax & Audit',
            desc: 'Rigorous financial management, tax planning, and compliance auditing to secure your corporate health.',
            img: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
            link: '/business-finance-services'
          }, {
            title: 'Legal, Permit & Visa',
            desc: 'Navigating Indonesian regulations seamlessly, from PT PMA establishment to KITAS and business licenses.',
            img: 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?ixlib=rb-4.0.3&auto=format&fit=crop&w=1212&q=80',
            link: '/legal-licensing-expat-services'
          }].map((service, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2,
            duration: 0.6
          }} className="group rounded-card overflow-hidden bg-navy-900 border border-navy-700 flex flex-col h-full" data-id="element-312">
                <div className="relative h-64 overflow-hidden" data-id="element-313">
                  <img src={service.img} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-80 group-hover:opacity-100" data-id="element-314" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900 to-transparent" data-id="element-315"></div>
                </div>
                <div className="p-8 flex flex-col flex-grow" data-id="element-316">
                  <h3 className="font-heading text-2xl font-semibold text-white mb-4" data-id="element-317">
                    {service.title}
                  </h3>
                  <p className="font-body text-gray-400 mb-8 flex-grow" data-id="element-318">
                    {service.desc}
                  </p>
                  <a href={service.link} className="inline-flex items-center gap-2 text-gold font-sub font-semibold uppercase tracking-wider text-sm hover:gap-3 transition-all" data-id="element-319">
                    Explore Service <ArrowRight size={16} data-id="element-320" />
                  </a>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      

      {/* Testimonials */}
      <section className="py-24 bg-navy-800" data-id="element-321">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-322">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-center max-w-3xl mx-auto mb-16" data-id="element-323">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-324">
              Client Success Stories
            </h2>
            <p className="font-body text-gray-400 text-lg" data-id="element-325">
              Hear from the investors and business owners who trust Marvesta
              Corp Bali.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-326">
            {[{
            quote: "Marvesta's property management team transformed our villa's operational efficiency. Their financial reporting is transparent, and their attention to guest experience is truly premium.",
            author: 'James W.',
            role: 'Luxury Villa Owner, Seminyak'
          }, {
            quote: 'Setting up our PT PMA was seamless thanks to their legal team. They handled all the licensing and KITAS applications with incredible professionalism and speed.',
            author: 'Sarah L.',
            role: 'Founder, Hospitality Group'
          }].map((testimonial, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2,
            duration: 0.5
          }} className="bg-navy-900 p-10 rounded-card border border-navy-700 relative" data-id="element-327">
                <div className="text-gold opacity-20 absolute top-6 right-8 font-display text-8xl leading-none" data-id="element-328">
                  "
                </div>
                <p className="font-body text-gray-300 text-lg leading-relaxed mb-8 relative z-10" data-id="element-329">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-4" data-id="element-330">
                  <div className="w-12 h-12 bg-navy-700 rounded-full flex items-center justify-center text-gold font-display text-xl font-bold" data-id="element-331">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div data-id="element-332">
                    <h4 className="font-heading text-xl font-semibold text-white" data-id="element-333">
                      {testimonial.author}
                    </h4>
                    <p className="font-sub text-sm text-gray-400" data-id="element-334">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-navy-900" data-id="element-335">
        <div className="max-w-content mx-auto px-6 md:px-12 flex flex-col lg:flex-row gap-16" data-id="element-336">
          <div className="lg:w-1/3" data-id="element-337">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-338">
              Frequently Asked Questions
            </h2>
            <p className="font-body text-gray-400 text-lg mb-8" data-id="element-339">
              Find answers to common questions about our corporate services and
              establishing a business in Bali.
            </p>
            <Button to="/contact" variant="outline" data-id="element-340">
              Ask a Question
            </Button>
          </div>

          <div className="lg:w-2/3 space-y-4" data-id="element-341">
            {[{
            q: 'How long does it take to establish a PT PMA in Bali?',
            a: 'The standard process for establishing a PT PMA (Foreign Owned Company) typically takes 4-6 weeks, provided all documentation is complete. Our legal team expedites this process by ensuring all requirements are met accurately from day one.'
          }, {
            q: 'What is included in your property management service?',
            a: 'Our comprehensive service includes operational management, staff training, revenue optimization, financial reporting, tax compliance, and guest experience management. We provide monthly transparent reports to owners.'
          }, {
            q: 'Can you assist with Investor KITAS?',
            a: 'Yes, we handle the entire Investor KITAS process, from initial application to final issuance, ensuring full compliance with Indonesian immigration laws.'
          }, {
            q: 'Do you provide external audit services?',
            a: 'Absolutely. We offer independent external audits, internal audits, and financial reviews to ensure your company meets all Indonesian regulatory and tax compliance standards.'
          }].map((faq, i) => <div key={i} className="border border-navy-700 rounded-card overflow-hidden bg-navy-800" data-id="element-342">
                <button className="w-full px-8 py-6 text-left flex justify-between items-center focus:outline-none" onClick={() => setActiveFaq(activeFaq === i ? null : i)} data-id="element-343">
                  <span className="font-heading text-xl font-semibold text-white pr-8" data-id="element-344">
                    {faq.q}
                  </span>
                  <ChevronDown className={`text-gold transition-transform duration-300 shrink-0 ${activeFaq === i ? 'rotate-180' : ''}`} data-id="element-345" />
                </button>
                <AnimatePresence data-id="element-346">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-347">
                      <div className="px-8 pb-6 text-gray-400 font-body leading-relaxed border-t border-navy-700 pt-4" data-id="element-348">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden" data-id="element-349">
        <div className="absolute inset-0 z-0" data-id="element-350">
          <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80" alt="Corporate Office" className="w-full h-full object-cover opacity-20" data-id="element-351" />
          <div className="absolute inset-0 bg-navy-900/90" data-id="element-352"></div>
        </div>

        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10 text-center" data-id="element-353">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8" data-id="element-354">
            Ready to Elevate Your Business in Bali?
          </h2>
          <p className="font-body text-xl text-gray-300 mb-10" data-id="element-355">
            Schedule a free consultation with our expert advisors to discuss
            your property, financial, or legal needs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-356">
            <Button to="/contact" data-id="element-357">Book a Free Consultation</Button>
            <Button href="https://wa.me/6282326357189" variant="outline" icon={<ArrowRight size={18} data-id="element-359" />} data-id="element-358">
              Contact via WhatsApp
            </Button>
          </div>
        </div>
      </section>
    </div>;
}